import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import { DataTable } from '@/components/organisms/DataTable';
import {
    type FeedingDashboardConfig,
    type FeedingPen,
    penLabel,
    type WarningMetric,
    warningTargetOf,
} from '@/domain/feedingDashboard';

import { ClosingDayLabel } from '../ClosingDayLabel';
import { dashboardLocale } from '../dashboardLocale';
import { systemLabels } from '../systemLabel';

import { PenTableScrollButtons } from './PenTableScrollButtons';
import { penComparisonRowData } from './penComparisonCells';
import { penComparisonColumns } from './penComparisonColumns';
import { formatTarget } from './penComparisonFormat';

import styles from './PenComparisonTable.module.scss';

interface PenComparisonTableProps {
    pens: FeedingPen[];
    /**
     * Unique label per pen id (`penDisplayNames` over the period's pens), so a historical
     * pen sharing a name with the current one reads as a different row.
     */
    penNames?: ReadonlyMap<number, string>;
    config: FeedingDashboardConfig;
    /**
     * The day every row is read on, named beside the title (U13). Optional only so the
     * tests of the columns need not state it; the page always passes it.
     */
    closingDay?: string | null;
    /**
     * Whether any selected pen has a feeding camera (IPS req 27). Controls only whether
     * the Pellet COLUMN exists: a supported pen with no pellet data for the period
     * shows a dash, since that is a data gap rather than a missing capability.
     */
    fallthroughSupported: boolean;
}

/**
 * The one target every shown pen is judged against, or null when pen overrides make them
 * differ, in which case a single number in the footnote would misstate some rows. Read
 * through the warning rule, so the footnote states the target the rows were compared with.
 */
function sharedTarget(
    pens: FeedingPen[],
    site: FeedingDashboardConfig,
    metric: WarningMetric
): number | null {
    const targets = new Set(pens.map((pen) => warningTargetOf(pen.config, metric).target));
    if (targets.size === 0) return warningTargetOf(site, metric).target;
    return targets.size === 1 ? [...targets][0] : null;
}

/** Per-pen comparison of planning-system and Bioscope metrics (IPS 2.6). */
export function PenComparisonTable(props: PenComparisonTableProps) {
    const { pens, penNames, config, closingDay = null, fallthroughSupported } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    // IPS 40: these columns describe the customer's own planning system, so they carry
    // its name rather than a generic one.
    const system = systemLabels(t, config.pmsSystemName);
    const fcrTarget = sharedTarget(pens, config, 'fcr');
    const rgiTarget = sharedTarget(pens, config, 'rgi');
    const varies = t('feedingModule.table.targetVaries');
    const title = t('feedingModule.table.title');
    const weightIntervalNoteId = useId();
    const scrollerRef = useRef<HTMLElement>(null);
    const columns = penComparisonColumns({ t, system, fallthroughSupported, weightIntervalNoteId });
    const rows = pens.map((pen) =>
        penComparisonRowData(pen, penLabel(penNames, pen), system, t, locale)
    );

    return (
        <Card>
            <div className={styles.titleRow}>
                <div className={styles.titleGroup}>
                    <Overline as="h2" size="md" className={styles.title}>
                        {title}
                    </Overline>
                    <ClosingDayLabel closingDay={closingDay} />
                </div>
                <PenTableScrollButtons scroller={scrollerRef} />
            </div>
            {/* Still wider than a narrow page, so the scroller is a region named like the
                title; the pen column stays pinned while it scrolls, and the buttons above
                scroll it from the keyboard. Named by aria-label rather than by the
                heading's id: a useId id holds colons, which jsdom's selector engine
                rejects when a stylesheet uses :has(). */}
            <section ref={scrollerRef} className={styles.scroll} aria-label={title}>
                <DataTable
                    data={rows}
                    columns={columns}
                    getRowId={(row) => String(row.pen.cageId)}
                    enableSorting={false}
                    hover={false}
                    tableClassName={styles.table}
                    getRowProps={(row) => ({
                        className: classNames(styles.row, {
                            [styles.warnedRow]: row.original.warnings.length > 0,
                        }),
                    })}
                />
            </section>
            <p className={styles.footnote}>
                {t('feedingModule.table.footnote', {
                    target: fcrTarget == null ? varies : formatTarget(fcrTarget, 'fcr', locale),
                    rgiTarget: rgiTarget == null ? varies : formatTarget(rgiTarget, 'rgi', locale),
                })}
            </p>
            <p id={weightIntervalNoteId} className={styles.footnote}>
                {t('feedingModule.table.confWeightNote')}
            </p>
        </Card>
    );
}
